import { useRef, useEffect } from "react";
import { string, number, bool, func } from "prop-types";
import Headline from "../Headline/component";
import CardImageWrap from "../CardImageWrap/component";
import { Label } from "../../../Label/component";
import Description from "../../../Description/component";
import LinkWrap from "../../../LinkWrap/component";
import CallToAction from "../CallToAction/component";
import classNames from "classnames";
import { createLinkProps } from "../../../../utils/dataTransform/utils";
import { normalizeClass } from "../../../../utils/path/library";
import { useImageDerivatives } from "../../../../hooks/useImageDerivatives";
import { DEFAULT_DERIVATIVE_WIDTHS } from "../../../../hooks/useImageDerivatives/constants";
import { usePreviewBase } from "../../../../hooks/usePreviewBase";
import { SRCSET_IMAGE_BREAKPOINTS } from "./constants";

/**
 * This card is shown in Featured areas of the site, on Landing pages and Story pages (e.g. "More from CBC")
 */
const CardShow = ({
    external,
    title,
    url,
    className,
    description,
    flag,
    flagId,
    imageURL,
    headlineId,
    descriptionId,
    sourceId,
    cardClickHandler,
    focus,
    videoCardOverlayType,
    videoDuration,
    videoAirDate,
    callToActionText,
    itemType,
    videoStreamType,
}) => {
    const domNode = useRef(null);

    useEffect(() => {
        if (focus !== undefined) {
            domNode.current.focus();
        }
    });

    const flagClassName = normalizeClass("flag", flag);
    const classes = classNames("card", flagClassName, "cardShow", className);

    const previewBaseImageURL = usePreviewBase(imageURL);
    const [srcSet] = useImageDerivatives(
        previewBaseImageURL,
        DEFAULT_DERIVATIVE_WIDTHS
    );

    const imageProps = {
        srcSet,
        sizes: SRCSET_IMAGE_BREAKPOINTS,
        url: previewBaseImageURL,
    };

    const videoLinkImageOverlayProps = {
        videoCardOverlayType,
        videoDuration,
        videoAirDate,
        videoStreamType,
    };

    const linkProps = createLinkProps({
        sourceId,
        external,
        url,
        cardClickHandler,
    });

    return (
        <LinkWrap
            className={classes}
            {...linkProps}
            data-cy={`type-${itemType}`}
            data-testid="card-show"
            ref={domNode}
        >
            <CardImageWrap {...{ imageProps, videoLinkImageOverlayProps }} />
            <div className="contentWrapper">
                <div className="card-content">
                    <div className="card-content-top">
                        <Label id={flagId} flag={flag} external={external} />
                        <Headline id={headlineId} text={title} />

                        {typeof description !== "undefined" && (
                            <Description
                                id={descriptionId}
                                text={description}
                            />
                        )}
                    </div>
                    <div className="card-content-bottom">
                        {typeof callToActionText !== "undefined" && (
                            <CallToAction text={callToActionText} />
                        )}
                    </div>
                </div>
            </div>
        </LinkWrap>
    );
};

CardShow.propTypes = {
    // Is the URL external (in which case we don't handle it)?
    external: bool.isRequired,
    // The headline to display.
    title: string.isRequired,
    // The URL that the card links to
    url: string.isRequired,
    // The image URL of the image
    imageURL: string.isRequired,
    // Item ID from original source, e.g. CMS.
    sourceId: string.isRequired,

    // OPTIONAL
    category: string,
    className: string,
    itemType: string,
    description: string,
    flag: string,
    flagId: string,
    headlineId: string,
    descriptionId: string,
    cardClickHandler: func,
    focus: bool,
    videoCardOverlayType: string,
    videoDuration: number,
    videoAirDate: number,
    callToActionText: string,
    videoStreamType: string,
};

export default CardShow;
